import Modal from 'bootstrap/js/src/modal.js';

let zoomLevel = 1;
let isDragging = false;
let startX = 0;
let startY = 0;
let translateX = 0;
let translateY = 0;
let zoomModal: any = null;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export function initImageZoom() {
    const zoomableImages = document.querySelectorAll('.zoomable-image');
    const zoomImage = document.getElementById('zoomImage') as HTMLImageElement;
    const zoomContainer = document.getElementById('zoomContainer');
    const zoomModalEl = document.getElementById('imageZoomModal');

    if (!zoomModalEl || !zoomImage || !zoomContainer) return;

    zoomModal = new Modal(zoomModalEl);

    // Click on image to open zoom modal
    zoomableImages.forEach(img => {
        img.addEventListener('click', (e) => {
            const target = e.currentTarget as HTMLImageElement;
            const zoomSrc = target.dataset.zoomSrc || target.src;

            // Reset zoom state
            zoomLevel = 1;
            translateX = 0;
            translateY = 0;

            // Set image source and show modal
            zoomImage.src = zoomSrc;
            updateTransform(zoomImage);
            zoomModal.show();
        });
    });

    // Zoom controls
    document.getElementById('zoomIn')?.addEventListener('click', () => {
        zoomLevel = Math.min(MAX_ZOOM, zoomLevel + ZOOM_STEP);
        updateTransform(zoomImage);
    });

    document.getElementById('zoomOut')?.addEventListener('click', () => {
        zoomLevel = Math.max(MIN_ZOOM, zoomLevel - ZOOM_STEP);
        updateTransform(zoomImage);
    });

    document.getElementById('zoomReset')?.addEventListener('click', () => {
        zoomLevel = 1;
        translateX = 0;
        translateY = 0;
        updateTransform(zoomImage);
    });

    // Mouse wheel zoom
    zoomContainer.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        zoomLevel = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + delta));
        updateTransform(zoomImage);
    }, { passive: false });

    // Drag to pan
    zoomImage.addEventListener('mousedown', (e) => {
        if (zoomLevel <= 1) return;
        isDragging = true;
        startX = e.clientX - translateX;
        startY = e.clientY - translateY;
        zoomImage.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        translateX = e.clientX - startX;
        translateY = e.clientY - startY;
        updateTransform(zoomImage);
    });

    document.addEventListener('mouseup', () => {
        isDragging = false;
        zoomImage.style.cursor = zoomLevel > 1 ? 'grab' : 'zoom-in';
    });

    // Touch support for mobile
    let lastTouchDistance = 0;

    zoomImage.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
            // Pinch to zoom
            lastTouchDistance = getTouchDistance(e.touches);
        } else if (e.touches.length === 1 && zoomLevel > 1) {
            // Single touch drag
            isDragging = true;
            startX = e.touches[0].clientX - translateX;
            startY = e.touches[0].clientY - translateY;
        }
    }, { passive: true });

    zoomImage.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2) {
            // Pinch to zoom
            const currentDistance = getTouchDistance(e.touches);
            const delta = (currentDistance - lastTouchDistance) * 0.01;
            zoomLevel = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + delta));
            lastTouchDistance = currentDistance;
            updateTransform(zoomImage);
        } else if (e.touches.length === 1 && isDragging) {
            // Single touch drag
            translateX = e.touches[0].clientX - startX;
            translateY = e.touches[0].clientY - startY;
            updateTransform(zoomImage);
        }
    }, { passive: true });

    zoomImage.addEventListener('touchend', () => {
        isDragging = false;
        lastTouchDistance = 0;
    });

    // Reset on modal close
    zoomModalEl.addEventListener('hidden.bs.modal', () => {
        zoomLevel = 1;
        translateX = 0;
        translateY = 0;
        zoomImage.src = '';
    });

    // Double click to toggle zoom
    zoomImage.addEventListener('dblclick', () => {
        if (zoomLevel > 1) {
            zoomLevel = 1;
            translateX = 0;
            translateY = 0;
        } else {
            zoomLevel = 2;
        }
        updateTransform(zoomImage);
    });
}

function updateTransform(img: HTMLImageElement) {
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoomLevel})`;
    img.style.cursor = zoomLevel > 1 ? 'grab' : 'zoom-in';
}

function getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
}
